import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { API, TOKEN_KEY } from "../lib/api";
import { useAuth } from "./AuthContext";

const LiveContext = createContext({ subscribe: () => () => {}, connected: false });

export function LiveProvider({ children }) {
  const { user } = useAuth();
  const subs = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const emit = (ev) => subs.current.forEach((fn) => fn(ev));

  useEffect(() => {
    if (!user) return undefined;
    let ws; let retry; let ping; let poll; let stopped = false;
    const connect = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token || "")}`);
      ws.onopen = () => {
        setConnected(true);
        clearInterval(poll);
        emit({ type: "reconnected" });
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onmessage = (m) => {
        if (m.data === "pong") return;
        try { emit(JSON.parse(m.data)); } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(ping);
        clearInterval(poll);
        poll = setInterval(() => emit({ type: "poll" }), 4000);
        if (!stopped) retry = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry); clearInterval(ping); clearInterval(poll);
      if (ws) ws.close();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  return <LiveContext.Provider value={{ subscribe, connected }}>{children}</LiveContext.Provider>;
}

export const useLiveStatus = () => useContext(LiveContext).connected;

export function useLive(handler) {
  const { subscribe } = useContext(LiveContext);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    let t;
    const unsub = subscribe((ev) => {
      clearTimeout(t);
      t = setTimeout(() => ref.current(ev), 250);
    });
    return () => { clearTimeout(t); unsub(); };
  }, [subscribe]);
}
